import { Button } from '@repo/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu'
import { Textarea } from '@repo/ui/components/ui/textarea'
import { cn } from '@repo/ui/lib/utils'
import {
  CheckCircle2,
  ChevronDown,
  CloudSun,
  DoorOpen,
  Eye,
  FileText,
  Loader2,
  MessageSquareText,
  Pencil,
  RotateCcw,
  WandSparkles,
} from 'lucide-react'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import {
  AppWorkspacePane,
  AppWorkspacePaneHeader,
  AppWorkspacePanel,
  AppWorkspaceSidebarDetailSplit,
} from '@/components/app/workspace-shell'
import { MarkdownPreview } from '@/components/ui/markdown-preview'
import {
  CONFIRMATION_TEMPLATE_TOKENS,
  normalizeOperatorBookingTheme,
  type OperatorBookingPageTheme,
} from '@/lib/booking-page-customization'
import type { OperatorConfigurationState } from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'

type PolicyEditorId =
  | 'booking-intro'
  | 'access-instructions'
  | 'weather-policy'
  | 'cancellation-policy'
  | 'confirmation-headline'
  | 'confirmation-body'

interface PolicyDraft {
  bookingCopy: OperatorBookingPageTheme['bookingCopy']
  confirmation: OperatorBookingPageTheme['confirmation']
}

interface PolicyEditorItem {
  id: PolicyEditorId
  label: string
  shortLabel: string
  description: string
  previewLabel: string
  value: string
  onChange: (value: string) => void
  rows?: number
  hint?: string
  placeholder: string
  icon: ReactNode
  templates: TemplateOption[]
}

interface TemplateOption {
  label: string
  value: string
}

const confirmationTemplateHint = CONFIRMATION_TEMPLATE_TOKENS.map((token) => `{{${token}}}`).join(
  ', '
)

export function BookingInfoWorkspace({
  configuration,
  onSaved,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
}) {
  const operator = configuration.operator
  const [draft, setDraft] = useState<PolicyDraft>(() =>
    buildPolicyDraft(operator?.bookingPageTheme)
  )
  const [activePolicyEditorId, setActivePolicyEditorId] = useState<PolicyEditorId>('booking-intro')
  const updateOperator = trpc.operator.update.useMutation({
    onSuccess: async () => {
      toast.success('Booking info saved')
      await Promise.resolve(onSaved?.())
    },
    onError: (error) => toast.error('Could not save booking info', { description: error.message }),
  })

  useEffect(() => {
    setDraft(buildPolicyDraft(operator?.bookingPageTheme))
  }, [operator?.bookingPageTheme])

  const savedDraft = useMemo(
    () => buildPolicyDraft(operator?.bookingPageTheme),
    [operator?.bookingPageTheme]
  )
  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedDraft)

  const setBookingCopyField = (field: keyof PolicyDraft['bookingCopy']) => (value: string) => {
    setDraft((current) => ({
      ...current,
      bookingCopy: {
        ...current.bookingCopy,
        [field]: value,
      },
    }))
  }

  const setConfirmationField = (field: keyof PolicyDraft['confirmation']) => (value: string) => {
    setDraft((current) => ({
      ...current,
      confirmation: {
        ...current.confirmation,
        [field]: value,
      },
    }))
  }

  const saveBookingInfo = () => {
    if (!operator) return

    const baseTheme = normalizeOperatorBookingTheme(operator.bookingPageTheme)
    updateOperator.mutate({
      bookingPageTheme: {
        ...baseTheme,
        bookingCopy: draft.bookingCopy,
        confirmation: draft.confirmation,
      },
    })
  }

  const items: PolicyEditorItem[] = [
    {
      id: 'booking-intro',
      label: 'Booking intro',
      shortLabel: 'Intro',
      description: 'Opening note on the booking page.',
      previewLabel: 'Before service selection',
      value: draft.bookingCopy.intro,
      onChange: setBookingCopyField('intro'),
      placeholder: 'Tell customers what to expect before they choose a service or request a visit.',
      icon: <MessageSquareText className="size-4" />,
      templates: [
        {
          label: 'Warm welcome',
          value:
            '## Welcome\n\nChoose the service that fits your visit. I will review the booking details and arrive within the selected service window.',
        },
        {
          label: 'Mobile service note',
          value:
            '## Before you book\n\nPlease choose the service location carefully so travel time and availability stay accurate.',
        },
      ],
    },
    {
      id: 'access-instructions',
      label: 'Access instructions',
      shortLabel: 'Access',
      description: 'Arrival, parking, gate, or entry guidance.',
      previewLabel: 'During appointment details',
      value: draft.bookingCopy.accessInstructions,
      onChange: setBookingCopyField('accessInstructions'),
      placeholder: 'Share parking, gate, entry, pets, or prep details that apply broadly.',
      icon: <DoorOpen className="size-4" />,
      templates: [
        {
          label: 'Access checklist',
          value:
            '- Share any gate or building access details.\n- Make sure parking is available nearby.\n- Keep pets secured before arrival.',
        },
        {
          label: 'Arrival note',
          value:
            'I will contact you if I need help finding the entrance or parking. Please keep your phone nearby before the appointment.',
        },
      ],
    },
    {
      id: 'weather-policy',
      label: 'Weather policy',
      shortLabel: 'Weather',
      description: 'How weather affects mobile visits.',
      previewLabel: 'Policy section',
      value: draft.bookingCopy.weatherPolicy,
      onChange: setBookingCopyField('weatherPolicy'),
      placeholder:
        'Explain when weather may require rescheduling and how customers will hear from you.',
      icon: <CloudSun className="size-4" />,
      templates: [
        {
          label: 'Reschedule if needed',
          value:
            'Weather may affect outdoor or driveway appointments. If conditions make the visit difficult, I will contact you to reschedule.',
        },
        {
          label: 'Customer safety',
          value:
            'Appointments may move when weather creates unsafe driving, access, or working conditions.',
        },
      ],
    },
    {
      id: 'cancellation-policy',
      label: 'Cancellation policy',
      shortLabel: 'Cancellation',
      description: 'What happens when plans change.',
      previewLabel: 'Policy section',
      value: draft.bookingCopy.cancellationPolicy,
      onChange: setBookingCopyField('cancellationPolicy'),
      placeholder: 'Set expectations for cancellations, late changes, and how much notice helps.',
      icon: <RotateCcw className="size-4" />,
      templates: [
        {
          label: 'Notice preferred',
          value:
            'Please cancel or reschedule as early as possible so the time can be offered to another customer.',
        },
        {
          label: 'Same-day changes',
          value:
            'Same-day changes may be limited once travel has started. Use your booking link to request changes when plans move.',
        },
      ],
    },
    {
      id: 'confirmation-headline',
      label: 'Confirmation headline',
      shortLabel: 'Headline',
      description: 'Top line after a booking is created.',
      previewLabel: 'Confirmation screen',
      value: draft.confirmation.headline,
      onChange: setConfirmationField('headline'),
      rows: 2,
      hint: confirmationTemplateHint,
      placeholder: 'Your {{serviceName}} visit is confirmed',
      icon: <CheckCircle2 className="size-4" />,
      templates: [
        {
          label: 'Service confirmed',
          value: 'Your {{serviceName}} visit is confirmed',
        },
        {
          label: 'See you soon',
          value: 'See you on {{date}}',
        },
      ],
    },
    {
      id: 'confirmation-body',
      label: 'Confirmation body',
      shortLabel: 'Confirmation',
      description: 'Follow-up detail after booking.',
      previewLabel: 'Confirmation screen',
      value: draft.confirmation.body,
      onChange: setConfirmationField('body'),
      hint: confirmationTemplateHint,
      placeholder: 'We will see you at {{address}} on {{date}}. Reply if anything changes.',
      icon: <FileText className="size-4" />,
      templates: [
        {
          label: 'Standard confirmation',
          value:
            'You are booked for **{{serviceName}}** on **{{date}}** at **{{startTime}}**.\n\nAddress: `{{address}}`',
        },
        {
          label: 'Change reminder',
          value:
            'Your booking is confirmed.\n\n- Service: {{serviceName}}\n- Date: {{date}}\n- Time: {{startTime}}\n\nUse your manage link if plans change.',
        },
      ],
    },
  ]

  return (
    <AppWorkspacePanel>
      <AppTopBarActions>
        <AppTopBarActionGroup>
          <Button
            type="button"
            size="sm"
            disabled={updateOperator.isPending || !isDirty}
            onClick={saveBookingInfo}
          >
            {updateOperator.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            Save
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>

      <PolicyEditor
        activeId={activePolicyEditorId}
        onActiveIdChange={setActivePolicyEditorId}
        items={items}
      />
    </AppWorkspacePanel>
  )
}

function buildPolicyDraft(
  theme: Partial<OperatorBookingPageTheme> | null | undefined
): PolicyDraft {
  const normalized = normalizeOperatorBookingTheme(theme)

  return {
    bookingCopy: normalized.bookingCopy,
    confirmation: normalized.confirmation,
  }
}

function PolicyEditor({
  activeId,
  onActiveIdChange,
  items,
}: {
  activeId: PolicyEditorId
  onActiveIdChange: (id: PolicyEditorId) => void
  items: PolicyEditorItem[]
}) {
  const activeItem = items.find((item) => item.id === activeId) ?? items[0]
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const completedCount = items.filter((item) => item.value.trim().length > 0).length
  const groups = [
    {
      label: 'Booking page',
      items: items.filter(
        (item) =>
          item.id === 'booking-intro' ||
          item.id === 'access-instructions' ||
          item.id === 'weather-policy' ||
          item.id === 'cancellation-policy'
      ),
    },
    {
      label: 'Confirmation',
      items: items.filter(
        (item) => item.id === 'confirmation-headline' || item.id === 'confirmation-body'
      ),
    },
  ]

  return (
    <AppWorkspaceSidebarDetailSplit>
      <AppWorkspacePane as="nav" surface="region" className="flex flex-col">
        <AppWorkspacePaneHeader
          eyebrow="Fields"
          description="Booking guidance and confirmation copy"
          actions={
            <div className="shrink-0 rounded-[4px] border border-border/70 bg-background/70 px-2.5 py-1 text-xs text-muted-foreground">
              {completedCount}/{items.length}
            </div>
          }
        />

        <div className="min-h-0 overflow-y-auto p-2">
          {groups.map((group) => (
            <div key={group.label} className="py-2 first:pt-0">
              <div className="px-2 pb-1 text-[11px] font-semibold tracking-[0.14em] text-muted-foreground uppercase">
                {group.label}
              </div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = item.id === activeItem.id
                  const hasValue = item.value.trim().length > 0

                  return (
                    <button
                      key={item.id}
                      type="button"
                      aria-current={active ? 'page' : undefined}
                      className={cn(
                        'group flex min-h-14 w-full cursor-pointer items-center gap-3 rounded-[6px] px-2.5 py-2 text-left transition-colors',
                        active
                          ? 'bg-background text-foreground ring-1 ring-border/80'
                          : 'text-muted-foreground hover:bg-background/70 hover:text-foreground'
                      )}
                      onClick={() => onActiveIdChange(item.id)}
                    >
                      <span
                        className={cn(
                          'grid size-8 shrink-0 place-items-center rounded-[5px] border transition-colors',
                          active
                            ? 'border-primary/30 bg-primary/8 text-primary'
                            : 'border-border/60 bg-background/65 group-hover:border-border'
                        )}
                      >
                        {item.icon}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">
                          {item.shortLabel}
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                          {item.description}
                        </span>
                      </span>
                      {hasValue ? (
                        <CheckCircle2 className="size-3.5 shrink-0 text-primary" />
                      ) : null}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </AppWorkspacePane>

      <AppWorkspacePane surface="muted" className="grid grid-rows-[auto_minmax(0,1fr)]">
        <AppWorkspacePaneHeader
          eyebrow={activeItem.label}
          description={activeItem.description}
          actions={
            <span className="shrink-0 rounded-[4px] border border-border/70 bg-background/70 px-2.5 py-1 text-xs text-muted-foreground">
              {activeItem.value.trim().length} chars
            </span>
          }
        />

        <div className="flex min-h-0 flex-col overflow-y-auto p-4 md:p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="gap-2">
                  <WandSparkles className="size-4" />
                  Templates
                  <ChevronDown className="size-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-64">
                {activeItem.templates.map((template) => (
                  <DropdownMenuItem
                    key={template.label}
                    className="cursor-pointer"
                    onClick={() => activeItem.onChange(template.value)}
                  >
                    {template.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="inline-flex rounded-[6px] border border-border/70 bg-background p-1">
              <Button
                type="button"
                variant={mode === 'edit' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 gap-2 rounded-[5px]"
                onClick={() => setMode('edit')}
              >
                <Pencil className="size-3.5" />
                Edit
              </Button>
              <Button
                type="button"
                variant={mode === 'preview' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-8 gap-2 rounded-[5px]"
                onClick={() => setMode('preview')}
              >
                <Eye className="size-3.5" />
                Preview
              </Button>
            </div>
          </div>

          {mode === 'edit' ? (
            <Textarea
              id={activeItem.id}
              rows={activeItem.rows ?? 7}
              value={activeItem.value}
              placeholder={activeItem.placeholder}
              className="mt-4 min-h-[22rem] resize-none rounded-[6px] border-border/70 bg-background/95 px-3 py-3 font-mono text-sm leading-6 shadow-none focus-visible:ring-2"
              onChange={(event) => activeItem.onChange(event.target.value)}
            />
          ) : (
            <div className="mt-4 min-h-[22rem] rounded-[6px] border border-border/70 bg-background/95 p-5">
              <MarkdownPreview markdown={activeItem.value || activeItem.placeholder} />
            </div>
          )}

          {activeItem.hint ? (
            <p className="mt-3 text-xs text-muted-foreground">Templates: {activeItem.hint}</p>
          ) : null}
        </div>
      </AppWorkspacePane>
    </AppWorkspaceSidebarDetailSplit>
  )
}
